import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [search, setSearch] = useState("");

  const limit = 50;

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [profileOrders, setProfileOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loadingProfileId, setLoadingProfileId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCustomers(page, debouncedSearch);
  }, [page, debouncedSearch]);

  const fetchCustomers = async (pageNumber, searchTerm = "") => {
    try {
      setLoading(true);

      const res = await adminApiRequest({
        script: "get-customers",
        data: {
          page: pageNumber,
          limit,
          search: searchTerm.trim(),
        },
      });

      setCustomers(res.customers || []);
      setTotalPages(res.pagination?.total_pages || 1);
      setTotalCustomers(res.pagination?.total_customers || 0);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setCustomers([]);
      setTotalPages(1);
      setTotalCustomers(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleViewProfile = async (customerId) => {
    if (loadingProfileId === customerId) return;

    try {
      setLoadingProfileId(customerId);
      setOrdersLoading(true);
      setExpandedOrderId(null);

      const customer = customers.find((c) => c.id === customerId);
      setProfileCustomer(customer || null);

      const res = await adminApiRequest({
        script: "get-customer-orders",
        data: { customer_id: customerId },
      });

      setProfileOrders(res.orders || []);
      setShowProfileModal(true);
    } catch (err) {
      console.error("Failed to fetch customer orders:", err);
    } finally {
      setOrdersLoading(false);
      setLoadingProfileId(null);
    }
  };

  const handleCloseModal = () => {
    setShowProfileModal(false);
    setProfileCustomer(null);
    setProfileOrders([]);
    setExpandedOrderId(null);
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const calculateSummary = () => {
    if (!profileOrders.length) {
      return { totalOrders: 0, lastOrderDate: "-", totalSpent: 0 };
    }

    const totalOrders = profileOrders.length;
    const lastOrderDate = new Date(
      profileOrders[0].created_at,
    ).toLocaleDateString("en-GB");
    const totalSpent = profileOrders.reduce(
      (sum, o) => sum + parseFloat(o.total || 0),
      0,
    );

    return { totalOrders, lastOrderDate, totalSpent };
  };

  const { totalOrders, lastOrderDate, totalSpent } = calculateSummary();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mb-6 flex flex-shrink-0 items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Customers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Total customers: {totalCustomers}
          </p>
        </div>

        <div className="w-full max-w-sm">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name, email or phone..."
            className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-800"></div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="h-full overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 border-b bg-slate-100">
                <tr className="text-xs uppercase tracking-wide text-slate-600">
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">First Name</th>
                  <th className="px-4 py-3 text-left">Last Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {customers.length > 0 ? (
                  customers.map((customer, index) => {
                    const isProfileLoading = loadingProfileId === customer.id;

                    return (
                      <tr
                        key={customer.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-4 py-3 text-slate-500">
                          {(page - 1) * limit + index + 1}
                        </td>

                        <td className="px-4 py-3">
                          {customer.first_name || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {customer.last_name || "—"}
                        </td>
                        <td className="px-4 py-3">{customer.email || "—"}</td>
                        <td className="px-4 py-3">{customer.phone || "—"}</td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-600 text-white hover:bg-green-700"
                              onClick={() =>
                                (window.location.href = `tel:${customer.phone}`)
                              }
                            >
                              <i className="fa fa-phone text-xs"></i>
                            </button>

                            <button
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
                              onClick={() => handleViewProfile(customer.id)}
                              disabled={isProfileLoading}
                            >
                              {isProfileLoading ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent"></span>
                              ) : (
                                <i className="fa fa-user text-xs"></i>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      No customers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <button
          className="rounded bg-slate-200 px-3 py-1 hover:bg-slate-300 disabled:opacity-40"
          disabled={page === 1 || loading}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>

        <div className="flex gap-1">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`rounded px-3 py-1 text-sm ${
                page === i + 1
                  ? "bg-slate-800 text-white"
                  : "bg-slate-200 hover:bg-slate-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <button
          className="rounded bg-slate-200 px-3 py-1 hover:bg-slate-300 disabled:opacity-40"
          disabled={page === totalPages || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      {showProfileModal && profileCustomer && (
        <Modal
          title={`${profileCustomer.first_name} ${profileCustomer.last_name}`}
          onClose={handleCloseModal}
          showCancel={false}
          showSubmitButton={false}
        >
          <div className="mb-6 grid grid-cols-3 gap-4 text-sm">
            <div className="rounded-lg bg-slate-100 p-3 text-center">
              <p className="text-xs text-slate-500">Orders</p>
              <p className="font-semibold">{totalOrders}</p>
            </div>

            <div className="rounded-lg bg-slate-100 p-3 text-center">
              <p className="text-xs text-slate-500">Last Order</p>
              <p className="font-semibold">{lastOrderDate}</p>
            </div>

            <div className="rounded-lg bg-slate-100 p-3 text-center">
              <p className="text-xs text-slate-500">Spent</p>
              <p className="font-semibold">£{totalSpent.toFixed(2)}</p>
            </div>
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-800"></div>
            </div>
          ) : (
            <div className="max-h-[420px] space-y-3 overflow-auto">
              {profileOrders.map((order) => {
                const isDelivery =
                  String(order.order_type || "")
                    .trim()
                    .toLowerCase() === "delivery";

                return (
                  <div
                    key={order.id}
                    className="cursor-pointer rounded-lg border p-4 hover:bg-slate-50"
                    onClick={() => toggleOrder(order.id)}
                  >
                    <div className="flex justify-between">
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>
                            {new Date(order.created_at).toLocaleDateString(
                              "en-GB",
                            )}
                          </span>

                          {order.order_number && (
                            <span className="font-medium">
                              #{order.order_number}
                            </span>
                          )}
                        </div>

                        <p className="font-semibold text-slate-900">
                          Order ID: {order.id}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="font-semibold">
                          £{parseFloat(order.total || 0).toFixed(2)}
                        </p>

                        <p className="text-xs text-slate-500">
                          {order.order_type}
                        </p>
                      </div>
                    </div>

                    {expandedOrderId === order.id && (
                      <div className="mt-4 space-y-4 text-sm">
                        {isDelivery && (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="font-semibold text-slate-800">
                              Delivery Details
                            </p>

                            {order.delivery_address && (
                              <p className="mt-2 text-slate-700">
                                <strong>Address:</strong>{" "}
                                {order.delivery_address}
                              </p>
                            )}

                            {order.postcode && (
                              <p className="text-slate-700">
                                <strong>Postcode:</strong> {order.postcode}
                              </p>
                            )}

                            {order.delivery_fee != null && (
                              <p className="text-slate-700">
                                <strong>Delivery Fee:</strong> £
                                {parseFloat(order.delivery_fee).toFixed(2)}
                              </p>
                            )}

                            {parseFloat(order.service_charge || 0) > 0 && (
                              <p className="text-slate-700">
                                <strong>Service Charge:</strong> £
                                {parseFloat(order.service_charge).toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="space-y-3">
                          {order.items.map((item) => {
                            const mainProteinIds = [
                              103, 104, 105, 106, 107, 108,
                            ];

                            const mainProteins =
                              item.proteins?.filter((p) =>
                                mainProteinIds.includes(Number(p.id)),
                              ) || [];

                            const extraProteins =
                              item.proteins?.filter(
                                (p) => !mainProteinIds.includes(Number(p.id)),
                              ) || [];

                            const isRemoved =
                              Number(item?.is_removed) === 1 ||
                              item?.is_removed === true;

                            return (
                              <div
                                key={item.id}
                                className={`relative rounded-lg border p-3 ${
                                  isRemoved
                                    ? "border-slate-300 bg-slate-100/90 opacity-60"
                                    : "border-slate-200"
                                }`}
                              >
                                {isRemoved && (
                                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                                      <i className="fa-solid fa-ban text-lg"></i>
                                    </div>
                                  </div>
                                )}

                                <div className="flex justify-between gap-4">
                                  <div>
                                    <p
                                      className={`font-medium ${
                                        isRemoved
                                          ? "text-slate-400 line-through"
                                          : "text-slate-900"
                                      }`}
                                    >
                                      {item.name} x {item.quantity}
                                    </p>

                                    {item.required_option_name && (
                                      <p
                                        className={`mt-1 ${
                                          isRemoved
                                            ? "text-slate-400 line-through"
                                            : "text-slate-600"
                                        }`}
                                      >
                                        <strong>Required:</strong>{" "}
                                        {item.required_option_name}
                                        {parseFloat(
                                          item.required_option_price || 0,
                                        ) > 0
                                          ? ` (£${parseFloat(item.required_option_price).toFixed(2)})`
                                          : ""}
                                      </p>
                                    )}

                                    {(item.selectedOptionalOptions || [])
                                      .length > 0 && (
                                      <div className="mt-2">
                                        <p
                                          className={`font-medium ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-700"
                                          }`}
                                        >
                                          Optional:
                                        </p>
                                        <ul
                                          className={`ml-5 list-disc ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-600"
                                          }`}
                                        >
                                          {item.selectedOptionalOptions.map(
                                            (opt, idx) => (
                                              <li
                                                key={idx}
                                                className={
                                                  isRemoved
                                                    ? "line-through"
                                                    : ""
                                                }
                                              >
                                                {opt.name} x {opt.quantity} (£
                                                {parseFloat(
                                                  opt.price || 0,
                                                ).toFixed(2)}
                                                )
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {(item.swallowOptions || []).length > 0 && (
                                      <div className="mt-2">
                                        <p
                                          className={`font-medium ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-700"
                                          }`}
                                        >
                                          Swallow:
                                        </p>
                                        <ul
                                          className={`ml-5 list-disc ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-600"
                                          }`}
                                        >
                                          {item.swallowOptions.map(
                                            (opt, idx) => (
                                              <li
                                                key={idx}
                                                className={
                                                  isRemoved
                                                    ? "line-through"
                                                    : ""
                                                }
                                              >
                                                {opt.name} x {opt.quantity} (£
                                                {parseFloat(
                                                  opt.price || 0,
                                                ).toFixed(2)}
                                                )
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {mainProteins.length > 0 && (
                                      <div className="mt-2">
                                        <p
                                          className={`font-medium ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-700"
                                          }`}
                                        >
                                          Protein(s):
                                        </p>
                                        <ul
                                          className={`ml-5 list-disc ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-600"
                                          }`}
                                        >
                                          {mainProteins.map((opt, idx) => (
                                            <li
                                              key={idx}
                                              className={
                                                isRemoved ? "line-through" : ""
                                              }
                                            >
                                              {opt.name} x {opt.quantity} (£
                                              {parseFloat(
                                                opt.price || 0,
                                              ).toFixed(2)}
                                              )
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {extraProteins.length > 0 && (
                                      <div className="mt-2">
                                        <p
                                          className={`font-medium ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-700"
                                          }`}
                                        >
                                          Extra:
                                        </p>
                                        <ul
                                          className={`ml-5 list-disc ${
                                            isRemoved
                                              ? "text-slate-400"
                                              : "text-slate-600"
                                          }`}
                                        >
                                          {extraProteins.map((opt, idx) => (
                                            <li
                                              key={idx}
                                              className={
                                                isRemoved ? "line-through" : ""
                                              }
                                            >
                                              {opt.name} x {opt.quantity} (£
                                              {parseFloat(
                                                opt.price || 0,
                                              ).toFixed(2)}
                                              )
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {isRemoved && (
                                      <p className="mt-2 text-xs font-semibold text-red-500">
                                        This item was removed from the order.
                                      </p>
                                    )}
                                  </div>

                                  <div
                                    className={`text-right font-semibold ${
                                      isRemoved
                                        ? "text-slate-400 line-through"
                                        : "text-slate-900"
                                    }`}
                                  >
                                    £
                                    {parseFloat(item.total_price || 0).toFixed(
                                      2,
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {!isDelivery &&
                          parseFloat(order.service_charge || 0) > 0 && (
                            <p>
                              <strong>Service:</strong> £
                              {parseFloat(order.service_charge).toFixed(2)}
                            </p>
                          )}

                        {order.notes && (
                          <p className="rounded-lg bg-amber-50 p-3 text-slate-700">
                            <strong>Notes:</strong> {order.notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
